// src/components/AdminJobApplicationsViewer.js
//latest code pushed 5.13.2026
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const AdminJobApplicationsViewer = ({ jobId, onClose }) => {
  const [applications, setApplications] = useState([]);
  const [analysisResults, setAnalysisResults] = useState({});
  const [isAnalyzingAll, setIsAnalyzingAll] = useState(false);
  const [rowLoading, setRowLoading] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  // Custom criteria state
  const [customCriteria, setCustomCriteria] = useState('');
  const [showCriteriaPanel, setShowCriteriaPanel] = useState(false);

  // Manual-entry UI state
  const [showManualForm, setShowManualForm] = useState(false);
  const [mName, setMName] = useState('');
  const [mEmail, setMEmail] = useState('');
  const [mPhone, setMPhone] = useState('');
  const [mResume, setMResume] = useState(null);
  const [mCover, setMCover] = useState(null);
  const [manualMsg, setManualMsg] = useState('');

  // ── helpers ──────────────────────────────────────────────────────────────
  const idStr = (v) => (v == null ? '' : String(v));

  const pickAnalysisText = (data, applicationId) => {
    if (data?.results && applicationId) {
      const byId = data.results[idStr(applicationId)];
      if (typeof byId === 'string' && byId.trim()) return byId;
    }
    for (const key of ['analysis', 'message', 'result', 'text']) {
      const val = data?.[key];
      if (typeof val === 'string' && val.trim()) return val;
    }
    return '✅ Analysis completed.';
  };

  const fetchApplications = async () => {
    setErrorMsg(null);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API}/admin/job/application/${jobId}`,
        { withCredentials: true }
      );
      setApplications(res.data.applications || []);
    } catch (err) {
      console.error('[AdminViewer] Fetch failed:', err);
      setErrorMsg('Failed to fetch applications.');
    }
  };

  useEffect(() => {
    if (jobId) fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // ── Delete Application ────────────────────────────────────────────────────
  const deleteApplication = async (applicationId, applicantName) => {
    if (!window.confirm(`Delete application from "${applicantName}"? This cannot be undone.`)) return;

    const key = idStr(applicationId);
    setDeletingId(key);
    setErrorMsg(null);

    try {
      await axios.delete(
        `${process.env.REACT_APP_API}/admin/job/application/${key}`,
        { withCredentials: true }
      );
      // Remove from local state immediately — no re-fetch needed
      setApplications(prev => prev.filter(a => idStr(a._id) !== key));
      setAnalysisResults(prev => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
    } catch (err) {
      console.error(`[Delete] Failed for ${key}:`, err);
      setErrorMsg(`Failed to delete application for "${applicantName}". Please try again.`);
    } finally {
      setDeletingId(null);
    }
  };

  // ── Bulk Analysis ─────────────────────────────────────────────────────────
  const analyzeAllApplications = async () => {
    setIsAnalyzingAll(true);
    setErrorMsg(null);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API}/admin/job/application/analyze/job/${jobId}`,
        { customCriteria },
        { withCredentials: true }
      );
      const results = res.data?.results || {};
      const normalized = Object.fromEntries(
        Object.entries(results).map(([k, v]) => [idStr(k), v])
      );
      setAnalysisResults(normalized);

      const { successCount, failCount } = res.data;
      if (failCount > 0) {
        setErrorMsg(`Bulk analysis complete — ${successCount} succeeded, ${failCount} failed.`);
      }
    } catch (err) {
      console.error('[AI Bulk Analysis] Failed:', err);
      const status = err?.response?.status;
      if (status === 504) {
        setErrorMsg('Bulk analysis timed out. Try analyzing applications one at a time.');
      } else {
        setErrorMsg(`Bulk analysis failed (HTTP ${status || 'network error'}). Please try again.`);
      }
    } finally {
      setIsAnalyzingAll(false);
    }
  };

  // ── Single Analysis ───────────────────────────────────────────────────────
  const analyzeSingleApplication = async (applicationId) => {
    const key = idStr(applicationId);
    setRowLoading(prev => ({ ...prev, [key]: true }));
    setErrorMsg(null);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API}/admin/job/application/analyze/${key}`,
        { customCriteria },
        { withCredentials: true }
      );
      const text = pickAnalysisText(res.data, key);
      setAnalysisResults(prev => ({ ...prev, [key]: text }));
    } catch (err) {
      console.error(`[AI Single Analysis] Failed for ${key}:`, err);
      setAnalysisResults(prev => ({
        ...prev,
        [key]: `❌ Error analyzing application (HTTP ${err?.response?.status || 'network error'}).`,
      }));
    } finally {
      setRowLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // ── Manual Candidate Submit ───────────────────────────────────────────────
  const submitManualCandidate = async (e) => {
    e.preventDefault();
    setManualMsg('');
    if (!mName || !mEmail || !mPhone || !mResume) {
      setManualMsg('Please complete all fields and attach a resume.');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('jobId', jobId);
      fd.append('name', mName);
      fd.append('email', mEmail);
      fd.append('phone', mPhone);
      fd.append('resume', mResume);
      if (mCover) fd.append('coverLetter', mCover); // optional
      const res = await axios.post(
        `${process.env.REACT_APP_API}/admin/job/manual-entry`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
      );
      if (res.data?.success) {
        setManualMsg('✅ Candidate added.');
        await fetchApplications();
        setMName(''); setMEmail(''); setMPhone('');
        setMResume(null); setMCover(null);
        setShowManualForm(false);
      } else {
        setManualMsg('❌ Failed to add candidate.');
      }
    } catch (err) {
      console.error('[Manual Entry] Failed:', err);
      setManualMsg('❌ Error adding candidate.');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={{ margin: 0 }}>Applications for Job ID: {jobId}</h3>
        <button onClick={onClose} style={styles.closeBtn}>✕ Close</button>
      </div>

      {errorMsg && (
        <div style={styles.errorBanner}>{errorMsg}</div>
      )}

      {/* ── Custom Criteria Panel ── */}
      <div style={styles.criteriaSection}>
        <button
          onClick={() => setShowCriteriaPanel(s => !s)}
          style={styles.btnSecondary}
        >
          {showCriteriaPanel ? '▲ Hide' : '▼ Set'} Custom Evaluation Criteria
        </button>
        {showCriteriaPanel && (
          <div style={styles.criteriaPanel}>
            <p style={styles.criteriaHint}>
              Enter specific skills, certifications, or experience items you want the AI to prioritize.
              Leave blank to use the default CPA firm / government auditing criteria.
            </p>
            <textarea
              value={customCriteria}
              onChange={e => setCustomCriteria(e.target.value)}
              placeholder={
                'Examples:\n' +
                '• Must have active CPA license\n' +
                '• DoD Secret clearance required\n' +
                '• Experience with SAP or Deltek\n' +
                '• GAAP and GAAS familiarity\n' +
                '• 3+ years federal government audit'
              }
              rows={6}
              style={styles.criteriaTextarea}
            />
            {customCriteria.trim() && (
              <div style={styles.criteriaActive}>
                ✅ Custom criteria active — will be applied to all analyses below
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Action buttons — always visible regardless of application count ── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '1rem 0' }}>
        {applications.length > 0 && (
          <button
            onClick={analyzeAllApplications}
            disabled={isAnalyzingAll}
            style={isAnalyzingAll ? styles.btnDisabled : styles.btnPrimary}
          >
            {isAnalyzingAll
              ? `⏳ Analyzing ${applications.length} applications…`
              : `🤖 Analyze All (${applications.length})`}
          </button>
        )}
        <button
          onClick={() => setShowManualForm(s => !s)}
          style={styles.btnGreen}
        >
          {showManualForm ? '✕ Cancel' : '+ Add Candidate Manually'}
        </button>
      </div>

      {/* ── Manual Entry Form — always available ── */}
      {showManualForm && (
        <form onSubmit={submitManualCandidate} style={styles.manualForm}>
          <div style={styles.manualGrid}>
            <input type="text" placeholder="Full Name" value={mName} onChange={e => setMName(e.target.value)} required style={styles.input} />
            <input type="email" placeholder="Email" value={mEmail} onChange={e => setMEmail(e.target.value)} required style={styles.input} />
            <input type="tel" placeholder="Phone" value={mPhone} onChange={e => setMPhone(e.target.value)} required style={styles.input} />
            <div />
            <div>
              <label style={styles.fileLabel}>Resume (PDF/DOC/DOCX)</label>
              <input type="file" accept=".pdf,.doc,.docx" onChange={e => setMResume(e.target.files[0] || null)} required />
            </div>
            <div>
              <label style={styles.fileLabel}>Cover Letter <span style={styles.optionalTag}>(optional)</span></label>
              <input type="file" accept=".pdf,.doc,.docx" onChange={e => setMCover(e.target.files[0] || null)} />
            </div>
          </div>
          <button type="submit" style={{ ...styles.btnPrimary, marginTop: 12 }}>Add Candidate</button>
          {manualMsg && (
            <div style={{ marginTop: 8, color: manualMsg.startsWith('✅') ? '#2e7d32' : '#b00020' }}>
              {manualMsg}
            </div>
          )}
        </form>
      )}

      {applications.length === 0 ? (
        <p style={{ color: '#888', fontStyle: 'italic', marginTop: '0.5rem' }}>
          No applications submitted yet. Use the button above to add a candidate manually.
        </p>
      ) : (
        <>
          {/* ── Application List ── */}
          <ul style={{ listStyle: 'none', padding: 0, marginTop: '1rem' }}>
            {applications.map(app => {
              const appId = idStr(app._id);
              const analysis = analysisResults[appId];
              const isAnalyzing = !!rowLoading[appId];
              const isDeleting = deletingId === appId;

              return (
                <li key={appId} style={styles.appCard}>
                  <div style={styles.appRow}>
                    {/* Applicant info */}
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: '2px 0' }}><strong>{app.name}</strong></p>
                      <p style={styles.smallText}>{app.email} | {app.phone}</p>
                      <span style={app.isManualEntry ? styles.badgeManual : styles.badgeApplicant}>
                        {app.isManualEntry ? 'Manually Added' : 'Applicant'}
                      </span>
                    </div>

                    {/* File links */}
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <a
                        href={`${process.env.REACT_APP_API}/uploads/jobApplications/${app.resumeFile}`}
                        target="_blank" rel="noreferrer"
                        style={styles.fileLink}
                      >
                        📄 Resume
                      </a>
                      {app.coverLetterFile ? (
                        <a
                          href={`${process.env.REACT_APP_API}/uploads/jobApplications/${app.coverLetterFile}`}
                          target="_blank" rel="noreferrer"
                          style={styles.fileLink}
                        >
                          📝 Cover
                        </a>
                      ) : (
                        <span style={styles.noCover}>No Cover Letter</span>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => analyzeSingleApplication(appId)}
                        disabled={isAnalyzing || isDeleting}
                        style={isAnalyzing ? styles.btnDisabled : styles.btnAnalyze}
                      >
                        {isAnalyzing ? '⏳ Analyzing…' : '🔍 Analyze'}
                      </button>

                      <button
                        onClick={() => deleteApplication(appId, app.name)}
                        disabled={isDeleting || isAnalyzing}
                        style={isDeleting ? styles.btnDisabled : styles.btnDelete}
                        title="Permanently delete this application"
                      >
                        {isDeleting ? '⏳ Deleting…' : '🗑 Delete'}
                      </button>
                    </div>
                  </div>

                  {/* AI Result */}
                  {analysis && (
                    <div style={styles.analysisBox}>
                      <div style={styles.analysisHeader}>
                        <strong>🤖 AI Analysis</strong>
                        {customCriteria.trim() && (
                          <span style={styles.criteriaTag}>Custom criteria applied</span>
                        )}
                      </div>
                      <pre style={styles.analysisText}>{analysis}</pre>
                    </div>
                  )}
                  <hr style={{ borderColor: '#e0e0e0', margin: '0.75rem 0 0' }} />
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  container: { marginTop: '1.5rem', border: '1px solid #ddd', padding: '1.25rem', borderRadius: 8, background: '#fafafa' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
  closeBtn: { padding: '4px 12px', border: '1px solid #ccc', borderRadius: 5, cursor: 'pointer', background: '#fff' },
  errorBanner: { margin: '0.5rem 0', padding: '0.5rem 0.75rem', background: '#fdecea', color: '#b71c1c', borderRadius: 6, fontSize: 14 },
  criteriaSection: { marginBottom: '1rem' },
  criteriaPanel: { marginTop: 8, padding: '1rem', background: '#fff', border: '1px solid #ddd', borderRadius: 6 },
  criteriaHint: { margin: '0 0 8px', fontSize: 13, color: '#555' },
  criteriaTextarea: { width: '100%', padding: '0.6rem', fontSize: 13, border: '1px solid #ccc', borderRadius: 5, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' },
  criteriaActive: { marginTop: 6, fontSize: 13, color: '#2e7d32', fontWeight: 600 },
  manualForm: { marginTop: '1rem', border: '1px dashed #bbb', padding: '1rem', borderRadius: 8, background: '#fff' },
  manualGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  input: { width: '100%', padding: '0.6rem', fontSize: 14, border: '1px solid #ccc', borderRadius: 5, boxSizing: 'border-box' },
  fileLabel: { display: 'block', fontSize: 13, marginBottom: 4, color: '#444' },
  optionalTag: { fontSize: 11, color: '#999', fontWeight: 'normal' },
  noCover: { fontSize: 12, color: '#aaa', fontStyle: 'italic' },
  appCard: { marginBottom: '1rem', background: '#fff', padding: '0.75rem', borderRadius: 6, border: '1px solid #e8e8e8' },
  appRow: { display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' },
  smallText: { margin: '2px 0', fontSize: 13, color: '#555' },
  fileLink: { fontSize: 13, color: '#1565c0', textDecoration: 'none' },
  analysisBox: { marginTop: '0.75rem', backgroundColor: '#f5f5f5', padding: '0.75rem 1rem', borderRadius: 6, border: '1px solid #e0e0e0' },
  analysisHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  analysisText: { whiteSpace: 'pre-wrap', margin: 0, fontSize: 13, lineHeight: 1.6, fontFamily: 'inherit' },
  criteriaTag: { fontSize: 11, padding: '2px 7px', background: '#e8f5e9', color: '#2e7d32', borderRadius: 10, fontWeight: 600 },
  badgeManual: { display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: '#fff3cd', color: '#856404', fontSize: 12 },
  badgeApplicant: { display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: '#e8f5e9', color: '#2e7d32', fontSize: 12 },
  btnPrimary: { padding: '0.6rem 1.2rem', fontSize: 14, border: 'none', borderRadius: 6, background: '#1565c0', color: '#fff', cursor: 'pointer' },
  btnSecondary: { padding: '0.5rem 1rem', fontSize: 13, border: '1px solid #1565c0', borderRadius: 6, background: '#fff', color: '#1565c0', cursor: 'pointer' },
  btnGreen: { padding: '0.6rem 1.2rem', fontSize: 14, border: 'none', borderRadius: 6, background: '#2e7d32', color: '#fff', cursor: 'pointer' },
  btnAnalyze: { padding: '0.4rem 0.9rem', fontSize: 13, border: 'none', borderRadius: 5, background: '#0277bd', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' },
  btnDelete: { padding: '0.4rem 0.9rem', fontSize: 13, border: 'none', borderRadius: 5, background: '#c62828', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' },
  btnDisabled: { padding: '0.4rem 0.9rem', fontSize: 13, border: 'none', borderRadius: 6, background: '#bbb', color: '#fff', cursor: 'not-allowed' },
};

export default AdminJobApplicationsViewer;







// // src/components/AdminJobApplicationsViewer.js
// import React, { useEffect, useState } from 'react';
// import axios from 'axios';

// const AdminJobApplicationsViewer = ({ jobId, onClose }) => {
//   const [applications, setApplications] = useState([]);
//   const [analysisResults, setAnalysisResults] = useState({});
//   const [isAnalyzingAll, setIsAnalyzingAll] = useState(false);
//   const [rowLoading, setRowLoading] = useState({});
//   const [deletingId, setDeletingId] = useState(null);
//   const [errorMsg, setErrorMsg] = useState(null);

//   // Custom criteria state
//   const [customCriteria, setCustomCriteria] = useState('');
//   const [showCriteriaPanel, setShowCriteriaPanel] = useState(false);

//   // Manual-entry UI state
//   const [showManualForm, setShowManualForm] = useState(false);
//   const [mName, setMName] = useState('');
//   const [mEmail, setMEmail] = useState('');
//   const [mPhone, setMPhone] = useState('');
//   const [mResume, setMResume] = useState(null);
//   const [mCover, setMCover] = useState(null);
//   const [manualMsg, setManualMsg] = useState('');

//   // ── helpers ──────────────────────────────────────────────────────────────
//   const idStr = (v) => (v == null ? '' : String(v));

//   const pickAnalysisText = (data, applicationId) => {
//     if (data?.results && applicationId) {
//       const byId = data.results[idStr(applicationId)];
//       if (typeof byId === 'string' && byId.trim()) return byId;
//     }
//     for (const key of ['analysis', 'message', 'result', 'text']) {
//       const val = data?.[key];
//       if (typeof val === 'string' && val.trim()) return val;
//     }
//     return '✅ Analysis completed.';
//   };

//   const fetchApplications = async () => {
//     setErrorMsg(null);
//     try {
//       const res = await axios.get(
//         `${process.env.REACT_APP_API}/admin/job/application/${jobId}`,
//         { withCredentials: true }
//       );
//       setApplications(res.data.applications || []);
//     } catch (err) {
//       console.error('[AdminViewer] Fetch failed:', err);
//       setErrorMsg('Failed to fetch applications.');
//     }
//   };

//   useEffect(() => {
//     if (jobId) fetchApplications();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [jobId]);

//   // ── Delete Application ────────────────────────────────────────────────────
//   const deleteApplication = async (applicationId, applicantName) => {
//     if (!window.confirm(`Delete application from "${applicantName}"? This cannot be undone.`)) return;

//     const key = idStr(applicationId);
//     setDeletingId(key);
//     setErrorMsg(null);

//     try {
//       await axios.delete(
//         `${process.env.REACT_APP_API}/admin/job/application/${key}`,
//         { withCredentials: true }
//       );
//       // Remove from local state immediately — no re-fetch needed
//       setApplications(prev => prev.filter(a => idStr(a._id) !== key));
//       setAnalysisResults(prev => {
//         const copy = { ...prev };
//         delete copy[key];
//         return copy;
//       });
//     } catch (err) {
//       console.error(`[Delete] Failed for ${key}:`, err);
//       setErrorMsg(`Failed to delete application for "${applicantName}". Please try again.`);
//     } finally {
//       setDeletingId(null);
//     }
//   };

//   // ── Bulk Analysis ─────────────────────────────────────────────────────────
//   const analyzeAllApplications = async () => {
//     setIsAnalyzingAll(true);
//     setErrorMsg(null);
//     try {
//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/application/analyze/job/${jobId}`,
//         { customCriteria },
//         { withCredentials: true }
//       );
//       const results = res.data?.results || {};
//       const normalized = Object.fromEntries(
//         Object.entries(results).map(([k, v]) => [idStr(k), v])
//       );
//       setAnalysisResults(normalized);

//       const { successCount, failCount } = res.data;
//       if (failCount > 0) {
//         setErrorMsg(`Bulk analysis complete — ${successCount} succeeded, ${failCount} failed.`);
//       }
//     } catch (err) {
//       console.error('[AI Bulk Analysis] Failed:', err);
//       const status = err?.response?.status;
//       if (status === 504) {
//         setErrorMsg('Bulk analysis timed out. Try analyzing applications one at a time.');
//       } else {
//         setErrorMsg(`Bulk analysis failed (HTTP ${status || 'network error'}). Please try again.`);
//       }
//     } finally {
//       setIsAnalyzingAll(false);
//     }
//   };

//   // ── Single Analysis ───────────────────────────────────────────────────────
//   const analyzeSingleApplication = async (applicationId) => {
//     const key = idStr(applicationId);
//     setRowLoading(prev => ({ ...prev, [key]: true }));
//     setErrorMsg(null);
//     try {
//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/application/analyze/${key}`,
//         { customCriteria },
//         { withCredentials: true }
//       );
//       const text = pickAnalysisText(res.data, key);
//       setAnalysisResults(prev => ({ ...prev, [key]: text }));
//     } catch (err) {
//       console.error(`[AI Single Analysis] Failed for ${key}:`, err);
//       setAnalysisResults(prev => ({
//         ...prev,
//         [key]: `❌ Error analyzing application (HTTP ${err?.response?.status || 'network error'}).`,
//       }));
//     } finally {
//       setRowLoading(prev => ({ ...prev, [key]: false }));
//     }
//   };

//   // ── Manual Candidate Submit ───────────────────────────────────────────────
//   const submitManualCandidate = async (e) => {
//     e.preventDefault();
//     setManualMsg('');
//     if (!mName || !mEmail || !mPhone || !mResume || !mCover) {
//       setManualMsg('Please complete all fields and attach both files.');
//       return;
//     }
//     try {
//       const fd = new FormData();
//       fd.append('jobId', jobId);
//       fd.append('name', mName);
//       fd.append('email', mEmail);
//       fd.append('phone', mPhone);
//       fd.append('resume', mResume);
//       fd.append('coverLetter', mCover);
//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/manual-entry`,
//         fd,
//         { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
//       );
//       if (res.data?.success) {
//         setManualMsg('✅ Candidate added.');
//         await fetchApplications();
//         setMName(''); setMEmail(''); setMPhone('');
//         setMResume(null); setMCover(null);
//         setShowManualForm(false);
//       } else {
//         setManualMsg('❌ Failed to add candidate.');
//       }
//     } catch (err) {
//       console.error('[Manual Entry] Failed:', err);
//       setManualMsg('❌ Error adding candidate.');
//     }
//   };

//   // ── Render ────────────────────────────────────────────────────────────────
//   return (
//     <div style={styles.container}>
//       <div style={styles.header}>
//         <h3 style={{ margin: 0 }}>Applications for Job ID: {jobId}</h3>
//         <button onClick={onClose} style={styles.closeBtn}>✕ Close</button>
//       </div>

//       {errorMsg && (
//         <div style={styles.errorBanner}>{errorMsg}</div>
//       )}

//       {/* ── Custom Criteria Panel ── */}
//       <div style={styles.criteriaSection}>
//         <button
//           onClick={() => setShowCriteriaPanel(s => !s)}
//           style={styles.btnSecondary}
//         >
//           {showCriteriaPanel ? '▲ Hide' : '▼ Set'} Custom Evaluation Criteria
//         </button>
//         {showCriteriaPanel && (
//           <div style={styles.criteriaPanel}>
//             <p style={styles.criteriaHint}>
//               Enter specific skills, certifications, or experience items you want the AI to prioritize.
//               Leave blank to use the default CPA firm / government auditing criteria.
//             </p>
//             <textarea
//               value={customCriteria}
//               onChange={e => setCustomCriteria(e.target.value)}
//               placeholder={
//                 'Examples:\n' +
//                 '• Must have active CPA license\n' +
//                 '• DoD Secret clearance required\n' +
//                 '• Experience with SAP or Deltek\n' +
//                 '• GAAP and GAAS familiarity\n' +
//                 '• 3+ years federal government audit'
//               }
//               rows={6}
//               style={styles.criteriaTextarea}
//             />
//             {customCriteria.trim() && (
//               <div style={styles.criteriaActive}>
//                 ✅ Custom criteria active — will be applied to all analyses below
//               </div>
//             )}
//           </div>
//         )}
//       </div>

//       {/* ── Action buttons — always visible regardless of application count ── */}
//       <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '1rem 0' }}>
//         {applications.length > 0 && (
//           <button
//             onClick={analyzeAllApplications}
//             disabled={isAnalyzingAll}
//             style={isAnalyzingAll ? styles.btnDisabled : styles.btnPrimary}
//           >
//             {isAnalyzingAll
//               ? `⏳ Analyzing ${applications.length} applications…`
//               : `🤖 Analyze All (${applications.length})`}
//           </button>
//         )}
//         <button
//           onClick={() => setShowManualForm(s => !s)}
//           style={styles.btnGreen}
//         >
//           {showManualForm ? '✕ Cancel' : '+ Add Candidate Manually'}
//         </button>
//       </div>

//       {/* ── Manual Entry Form — always available ── */}
//       {showManualForm && (
//         <form onSubmit={submitManualCandidate} style={styles.manualForm}>
//           <div style={styles.manualGrid}>
//             <input type="text" placeholder="Full Name" value={mName} onChange={e => setMName(e.target.value)} required style={styles.input} />
//             <input type="email" placeholder="Email" value={mEmail} onChange={e => setMEmail(e.target.value)} required style={styles.input} />
//             <input type="tel" placeholder="Phone" value={mPhone} onChange={e => setMPhone(e.target.value)} required style={styles.input} />
//             <div />
//             <div>
//               <label style={styles.fileLabel}>Resume (PDF/DOC/DOCX)</label>
//               <input type="file" accept=".pdf,.doc,.docx" onChange={e => setMResume(e.target.files[0] || null)} required />
//             </div>
//             <div>
//               <label style={styles.fileLabel}>Cover Letter (PDF/DOC/DOCX)</label>
//               <input type="file" accept=".pdf,.doc,.docx" onChange={e => setMCover(e.target.files[0] || null)} required />
//             </div>
//           </div>
//           <button type="submit" style={{ ...styles.btnPrimary, marginTop: 12 }}>Add Candidate</button>
//           {manualMsg && (
//             <div style={{ marginTop: 8, color: manualMsg.startsWith('✅') ? '#2e7d32' : '#b00020' }}>
//               {manualMsg}
//             </div>
//           )}
//         </form>
//       )}

//       {applications.length === 0 ? (
//         <p style={{ color: '#888', fontStyle: 'italic', marginTop: '0.5rem' }}>
//           No applications submitted yet. Use the button above to add a candidate manually.
//         </p>
//       ) : (
//         <>
//           {/* ── Application List ── */}
//           <ul style={{ listStyle: 'none', padding: 0, marginTop: '1rem' }}>
//             {applications.map(app => {
//               const appId = idStr(app._id);
//               const analysis = analysisResults[appId];
//               const isAnalyzing = !!rowLoading[appId];
//               const isDeleting = deletingId === appId;

//               return (
//                 <li key={appId} style={styles.appCard}>
//                   <div style={styles.appRow}>
//                     {/* Applicant info */}
//                     <div style={{ flex: 1 }}>
//                       <p style={{ margin: '2px 0' }}><strong>{app.name}</strong></p>
//                       <p style={styles.smallText}>{app.email} | {app.phone}</p>
//                       <span style={app.isManualEntry ? styles.badgeManual : styles.badgeApplicant}>
//                         {app.isManualEntry ? 'Manually Added' : 'Applicant'}
//                       </span>
//                     </div>

//                     {/* File links */}
//                     <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
//                       <a
//                         href={`${process.env.REACT_APP_API}/uploads/jobApplications/${app.resumeFile}`}
//                         target="_blank" rel="noreferrer"
//                         style={styles.fileLink}
//                       >
//                         📄 Resume
//                       </a>
//                       <a
//                         href={`${process.env.REACT_APP_API}/uploads/jobApplications/${app.coverLetterFile}`}
//                         target="_blank" rel="noreferrer"
//                         style={styles.fileLink}
//                       >
//                         📝 Cover
//                       </a>
//                     </div>

//                     {/* Action buttons */}
//                     <div style={{ display: 'flex', gap: 8 }}>
//                       <button
//                         onClick={() => analyzeSingleApplication(appId)}
//                         disabled={isAnalyzing || isDeleting}
//                         style={isAnalyzing ? styles.btnDisabled : styles.btnAnalyze}
//                       >
//                         {isAnalyzing ? '⏳ Analyzing…' : '🔍 Analyze'}
//                       </button>

//                       <button
//                         onClick={() => deleteApplication(appId, app.name)}
//                         disabled={isDeleting || isAnalyzing}
//                         style={isDeleting ? styles.btnDisabled : styles.btnDelete}
//                         title="Permanently delete this application"
//                       >
//                         {isDeleting ? '⏳ Deleting…' : '🗑 Delete'}
//                       </button>
//                     </div>
//                   </div>

//                   {/* AI Result */}
//                   {analysis && (
//                     <div style={styles.analysisBox}>
//                       <div style={styles.analysisHeader}>
//                         <strong>🤖 AI Analysis</strong>
//                         {customCriteria.trim() && (
//                           <span style={styles.criteriaTag}>Custom criteria applied</span>
//                         )}
//                       </div>
//                       <pre style={styles.analysisText}>{analysis}</pre>
//                     </div>
//                   )}
//                   <hr style={{ borderColor: '#e0e0e0', margin: '0.75rem 0 0' }} />
//                 </li>
//               );
//             })}
//           </ul>
//         </>
//       )}
//     </div>
//   );
// };

// // ── Styles ────────────────────────────────────────────────────────────────────
// const styles = {
//   container: { marginTop: '1.5rem', border: '1px solid #ddd', padding: '1.25rem', borderRadius: 8, background: '#fafafa' },
//   header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
//   closeBtn: { padding: '4px 12px', border: '1px solid #ccc', borderRadius: 5, cursor: 'pointer', background: '#fff' },
//   errorBanner: { margin: '0.5rem 0', padding: '0.5rem 0.75rem', background: '#fdecea', color: '#b71c1c', borderRadius: 6, fontSize: 14 },
//   criteriaSection: { marginBottom: '1rem' },
//   criteriaPanel: { marginTop: 8, padding: '1rem', background: '#fff', border: '1px solid #ddd', borderRadius: 6 },
//   criteriaHint: { margin: '0 0 8px', fontSize: 13, color: '#555' },
//   criteriaTextarea: { width: '100%', padding: '0.6rem', fontSize: 13, border: '1px solid #ccc', borderRadius: 5, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' },
//   criteriaActive: { marginTop: 6, fontSize: 13, color: '#2e7d32', fontWeight: 600 },
//   manualForm: { marginTop: '1rem', border: '1px dashed #bbb', padding: '1rem', borderRadius: 8, background: '#fff' },
//   manualGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
//   input: { width: '100%', padding: '0.6rem', fontSize: 14, border: '1px solid #ccc', borderRadius: 5, boxSizing: 'border-box' },
//   fileLabel: { display: 'block', fontSize: 13, marginBottom: 4, color: '#444' },
//   appCard: { marginBottom: '1rem', background: '#fff', padding: '0.75rem', borderRadius: 6, border: '1px solid #e8e8e8' },
//   appRow: { display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' },
//   smallText: { margin: '2px 0', fontSize: 13, color: '#555' },
//   fileLink: { fontSize: 13, color: '#1565c0', textDecoration: 'none' },
//   analysisBox: { marginTop: '0.75rem', backgroundColor: '#f5f5f5', padding: '0.75rem 1rem', borderRadius: 6, border: '1px solid #e0e0e0' },
//   analysisHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
//   analysisText: { whiteSpace: 'pre-wrap', margin: 0, fontSize: 13, lineHeight: 1.6, fontFamily: 'inherit' },
//   criteriaTag: { fontSize: 11, padding: '2px 7px', background: '#e8f5e9', color: '#2e7d32', borderRadius: 10, fontWeight: 600 },
//   badgeManual: { display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: '#fff3cd', color: '#856404', fontSize: 12 },
//   badgeApplicant: { display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: '#e8f5e9', color: '#2e7d32', fontSize: 12 },
//   btnPrimary: { padding: '0.6rem 1.2rem', fontSize: 14, border: 'none', borderRadius: 6, background: '#1565c0', color: '#fff', cursor: 'pointer' },
//   btnSecondary: { padding: '0.5rem 1rem', fontSize: 13, border: '1px solid #1565c0', borderRadius: 6, background: '#fff', color: '#1565c0', cursor: 'pointer' },
//   btnGreen: { padding: '0.6rem 1.2rem', fontSize: 14, border: 'none', borderRadius: 6, background: '#2e7d32', color: '#fff', cursor: 'pointer' },
//   btnAnalyze: { padding: '0.4rem 0.9rem', fontSize: 13, border: 'none', borderRadius: 5, background: '#0277bd', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' },
//   btnDelete: { padding: '0.4rem 0.9rem', fontSize: 13, border: 'none', borderRadius: 5, background: '#c62828', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' },
//   btnDisabled: { padding: '0.4rem 0.9rem', fontSize: 13, border: 'none', borderRadius: 6, background: '#bbb', color: '#fff', cursor: 'not-allowed' },
// };

// export default AdminJobApplicationsViewer;



// // src/components/AdminJobApplicationsViewer.js
// import React, { useEffect, useState } from 'react';
// import axios from 'axios';

// const AdminJobApplicationsViewer = ({ jobId, onClose }) => {
//   const [applications, setApplications] = useState([]);
//   const [analysisResults, setAnalysisResults] = useState({});
//   const [isAnalyzingAll, setIsAnalyzingAll] = useState(false);
//   const [rowLoading, setRowLoading] = useState({});
//   const [deletingId, setDeletingId] = useState(null);
//   const [errorMsg, setErrorMsg] = useState(null);

//   // Custom criteria state
//   const [customCriteria, setCustomCriteria] = useState('');
//   const [showCriteriaPanel, setShowCriteriaPanel] = useState(false);

//   // Manual-entry UI state
//   const [showManualForm, setShowManualForm] = useState(false);
//   const [mName, setMName] = useState('');
//   const [mEmail, setMEmail] = useState('');
//   const [mPhone, setMPhone] = useState('');
//   const [mResume, setMResume] = useState(null);
//   const [mCover, setMCover] = useState(null);
//   const [manualMsg, setManualMsg] = useState('');

//   // ── helpers ──────────────────────────────────────────────────────────────
//   const idStr = (v) => (v == null ? '' : String(v));

//   const pickAnalysisText = (data, applicationId) => {
//     if (data?.results && applicationId) {
//       const byId = data.results[idStr(applicationId)];
//       if (typeof byId === 'string' && byId.trim()) return byId;
//     }
//     for (const key of ['analysis', 'message', 'result', 'text']) {
//       const val = data?.[key];
//       if (typeof val === 'string' && val.trim()) return val;
//     }
//     return '✅ Analysis completed.';
//   };

//   const fetchApplications = async () => {
//     setErrorMsg(null);
//     try {
//       const res = await axios.get(
//         `${process.env.REACT_APP_API}/admin/job/application/${jobId}`,
//         { withCredentials: true }
//       );
//       setApplications(res.data.applications || []);
//     } catch (err) {
//       console.error('[AdminViewer] Fetch failed:', err);
//       setErrorMsg('Failed to fetch applications.');
//     }
//   };

//   useEffect(() => {
//     if (jobId) fetchApplications();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [jobId]);

//   // ── Delete Application ────────────────────────────────────────────────────
//   const deleteApplication = async (applicationId, applicantName) => {
//     if (!window.confirm(`Delete application from "${applicantName}"? This cannot be undone.`)) return;

//     const key = idStr(applicationId);
//     setDeletingId(key);
//     setErrorMsg(null);

//     try {
//       await axios.delete(
//         `${process.env.REACT_APP_API}/admin/job/application/${key}`,
//         { withCredentials: true }
//       );
//       // Remove from local state immediately — no re-fetch needed
//       setApplications(prev => prev.filter(a => idStr(a._id) !== key));
//       setAnalysisResults(prev => {
//         const copy = { ...prev };
//         delete copy[key];
//         return copy;
//       });
//     } catch (err) {
//       console.error(`[Delete] Failed for ${key}:`, err);
//       setErrorMsg(`Failed to delete application for "${applicantName}". Please try again.`);
//     } finally {
//       setDeletingId(null);
//     }
//   };

//   // ── Bulk Analysis ─────────────────────────────────────────────────────────
//   const analyzeAllApplications = async () => {
//     setIsAnalyzingAll(true);
//     setErrorMsg(null);
//     try {
//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/application/analyze/job/${jobId}`,
//         { customCriteria },
//         { withCredentials: true }
//       );
//       const results = res.data?.results || {};
//       const normalized = Object.fromEntries(
//         Object.entries(results).map(([k, v]) => [idStr(k), v])
//       );
//       setAnalysisResults(normalized);

//       const { successCount, failCount } = res.data;
//       if (failCount > 0) {
//         setErrorMsg(`Bulk analysis complete — ${successCount} succeeded, ${failCount} failed.`);
//       }
//     } catch (err) {
//       console.error('[AI Bulk Analysis] Failed:', err);
//       const status = err?.response?.status;
//       if (status === 504) {
//         setErrorMsg('Bulk analysis timed out. Try analyzing applications one at a time.');
//       } else {
//         setErrorMsg(`Bulk analysis failed (HTTP ${status || 'network error'}). Please try again.`);
//       }
//     } finally {
//       setIsAnalyzingAll(false);
//     }
//   };

//   // ── Single Analysis ───────────────────────────────────────────────────────
//   const analyzeSingleApplication = async (applicationId) => {
//     const key = idStr(applicationId);
//     setRowLoading(prev => ({ ...prev, [key]: true }));
//     setErrorMsg(null);
//     try {
//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/application/analyze/${key}`,
//         { customCriteria },
//         { withCredentials: true }
//       );
//       const text = pickAnalysisText(res.data, key);
//       setAnalysisResults(prev => ({ ...prev, [key]: text }));
//     } catch (err) {
//       console.error(`[AI Single Analysis] Failed for ${key}:`, err);
//       setAnalysisResults(prev => ({
//         ...prev,
//         [key]: `❌ Error analyzing application (HTTP ${err?.response?.status || 'network error'}).`,
//       }));
//     } finally {
//       setRowLoading(prev => ({ ...prev, [key]: false }));
//     }
//   };

//   // ── Manual Candidate Submit ───────────────────────────────────────────────
//   const submitManualCandidate = async (e) => {
//     e.preventDefault();
//     setManualMsg('');
//     if (!mName || !mEmail || !mPhone || !mResume || !mCover) {
//       setManualMsg('Please complete all fields and attach both files.');
//       return;
//     }
//     try {
//       const fd = new FormData();
//       fd.append('jobId', jobId);
//       fd.append('name', mName);
//       fd.append('email', mEmail);
//       fd.append('phone', mPhone);
//       fd.append('resume', mResume);
//       fd.append('coverLetter', mCover);
//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/manual-entry`,
//         fd,
//         { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
//       );
//       if (res.data?.success) {
//         setManualMsg('✅ Candidate added.');
//         await fetchApplications();
//         setMName(''); setMEmail(''); setMPhone('');
//         setMResume(null); setMCover(null);
//         setShowManualForm(false);
//       } else {
//         setManualMsg('❌ Failed to add candidate.');
//       }
//     } catch (err) {
//       console.error('[Manual Entry] Failed:', err);
//       setManualMsg('❌ Error adding candidate.');
//     }
//   };

//   // ── Render ────────────────────────────────────────────────────────────────
//   return (
//     <div style={styles.container}>
//       <div style={styles.header}>
//         <h3 style={{ margin: 0 }}>Applications for Job ID: {jobId}</h3>
//         <button onClick={onClose} style={styles.closeBtn}>✕ Close</button>
//       </div>

//       {errorMsg && (
//         <div style={styles.errorBanner}>{errorMsg}</div>
//       )}

//       {/* ── Custom Criteria Panel ── */}
//       <div style={styles.criteriaSection}>
//         <button
//           onClick={() => setShowCriteriaPanel(s => !s)}
//           style={styles.btnSecondary}
//         >
//           {showCriteriaPanel ? '▲ Hide' : '▼ Set'} Custom Evaluation Criteria
//         </button>
//         {showCriteriaPanel && (
//           <div style={styles.criteriaPanel}>
//             <p style={styles.criteriaHint}>
//               Enter specific skills, certifications, or experience items you want the AI to prioritize.
//               Leave blank to use the default CPA firm / government auditing criteria.
//             </p>
//             <textarea
//               value={customCriteria}
//               onChange={e => setCustomCriteria(e.target.value)}
//               placeholder={
//                 'Examples:\n' +
//                 '• Must have active CPA license\n' +
//                 '• DoD Secret clearance required\n' +
//                 '• Experience with SAP or Deltek\n' +
//                 '• GAAP and GAAS familiarity\n' +
//                 '• 3+ years federal government audit'
//               }
//               rows={6}
//               style={styles.criteriaTextarea}
//             />
//             {customCriteria.trim() && (
//               <div style={styles.criteriaActive}>
//                 ✅ Custom criteria active — will be applied to all analyses below
//               </div>
//             )}
//           </div>
//         )}
//       </div>

//       {applications.length === 0 ? (
//         <p>No applications submitted yet.</p>
//       ) : (
//         <>
//           <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '1rem 0' }}>
//             <button
//               onClick={analyzeAllApplications}
//               disabled={isAnalyzingAll}
//               style={isAnalyzingAll ? styles.btnDisabled : styles.btnPrimary}
//             >
//               {isAnalyzingAll
//                 ? `⏳ Analyzing ${applications.length} applications…`
//                 : `🤖 Analyze All (${applications.length})`}
//             </button>

//             <button
//               onClick={() => setShowManualForm(s => !s)}
//               style={styles.btnGreen}
//             >
//               {showManualForm ? '✕ Cancel' : '+ Add Candidate Manually'}
//             </button>
//           </div>

//           {/* ── Manual Entry Form ── */}
//           {showManualForm && (
//             <form onSubmit={submitManualCandidate} style={styles.manualForm}>
//               <div style={styles.manualGrid}>
//                 <input type="text" placeholder="Full Name" value={mName} onChange={e => setMName(e.target.value)} required style={styles.input} />
//                 <input type="email" placeholder="Email" value={mEmail} onChange={e => setMEmail(e.target.value)} required style={styles.input} />
//                 <input type="tel" placeholder="Phone" value={mPhone} onChange={e => setMPhone(e.target.value)} required style={styles.input} />
//                 <div />
//                 <div>
//                   <label style={styles.fileLabel}>Resume (PDF/DOC/DOCX)</label>
//                   <input type="file" accept=".pdf,.doc,.docx" onChange={e => setMResume(e.target.files[0] || null)} required />
//                 </div>
//                 <div>
//                   <label style={styles.fileLabel}>Cover Letter (PDF/DOC/DOCX)</label>
//                   <input type="file" accept=".pdf,.doc,.docx" onChange={e => setMCover(e.target.files[0] || null)} required />
//                 </div>
//               </div>
//               <button type="submit" style={{ ...styles.btnPrimary, marginTop: 12 }}>Add Candidate</button>
//               {manualMsg && (
//                 <div style={{ marginTop: 8, color: manualMsg.startsWith('✅') ? '#2e7d32' : '#b00020' }}>
//                   {manualMsg}
//                 </div>
//               )}
//             </form>
//           )}

//           {/* ── Application List ── */}
//           <ul style={{ listStyle: 'none', padding: 0, marginTop: '1rem' }}>
//             {applications.map(app => {
//               const appId = idStr(app._id);
//               const analysis = analysisResults[appId];
//               const isAnalyzing = !!rowLoading[appId];
//               const isDeleting = deletingId === appId;

//               return (
//                 <li key={appId} style={styles.appCard}>
//                   <div style={styles.appRow}>
//                     {/* Applicant info */}
//                     <div style={{ flex: 1 }}>
//                       <p style={{ margin: '2px 0' }}><strong>{app.name}</strong></p>
//                       <p style={styles.smallText}>{app.email} | {app.phone}</p>
//                       <span style={app.isManualEntry ? styles.badgeManual : styles.badgeApplicant}>
//                         {app.isManualEntry ? 'Manually Added' : 'Applicant'}
//                       </span>
//                     </div>

//                     {/* File links */}
//                     <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
//                       <a
//                         href={`${process.env.REACT_APP_API}/uploads/jobApplications/${app.resumeFile}`}
//                         target="_blank" rel="noreferrer"
//                         style={styles.fileLink}
//                       >
//                         📄 Resume
//                       </a>
//                       <a
//                         href={`${process.env.REACT_APP_API}/uploads/jobApplications/${app.coverLetterFile}`}
//                         target="_blank" rel="noreferrer"
//                         style={styles.fileLink}
//                       >
//                         📝 Cover
//                       </a>
//                     </div>

//                     {/* Action buttons */}
//                     <div style={{ display: 'flex', gap: 8 }}>
//                       <button
//                         onClick={() => analyzeSingleApplication(appId)}
//                         disabled={isAnalyzing || isDeleting}
//                         style={isAnalyzing ? styles.btnDisabled : styles.btnAnalyze}
//                       >
//                         {isAnalyzing ? '⏳ Analyzing…' : '🔍 Analyze'}
//                       </button>

//                       <button
//                         onClick={() => deleteApplication(appId, app.name)}
//                         disabled={isDeleting || isAnalyzing}
//                         style={isDeleting ? styles.btnDisabled : styles.btnDelete}
//                         title="Permanently delete this application"
//                       >
//                         {isDeleting ? '⏳ Deleting…' : '🗑 Delete'}
//                       </button>
//                     </div>
//                   </div>

//                   {/* AI Result */}
//                   {analysis && (
//                     <div style={styles.analysisBox}>
//                       <div style={styles.analysisHeader}>
//                         <strong>🤖 AI Analysis</strong>
//                         {customCriteria.trim() && (
//                           <span style={styles.criteriaTag}>Custom criteria applied</span>
//                         )}
//                       </div>
//                       <pre style={styles.analysisText}>{analysis}</pre>
//                     </div>
//                   )}
//                   <hr style={{ borderColor: '#e0e0e0', margin: '0.75rem 0 0' }} />
//                 </li>
//               );
//             })}
//           </ul>
//         </>
//       )}
//     </div>
//   );
// };

// // ── Styles ────────────────────────────────────────────────────────────────────
// const styles = {
//   container: { marginTop: '1.5rem', border: '1px solid #ddd', padding: '1.25rem', borderRadius: 8, background: '#fafafa' },
//   header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
//   closeBtn: { padding: '4px 12px', border: '1px solid #ccc', borderRadius: 5, cursor: 'pointer', background: '#fff' },
//   errorBanner: { margin: '0.5rem 0', padding: '0.5rem 0.75rem', background: '#fdecea', color: '#b71c1c', borderRadius: 6, fontSize: 14 },
//   criteriaSection: { marginBottom: '1rem' },
//   criteriaPanel: { marginTop: 8, padding: '1rem', background: '#fff', border: '1px solid #ddd', borderRadius: 6 },
//   criteriaHint: { margin: '0 0 8px', fontSize: 13, color: '#555' },
//   criteriaTextarea: { width: '100%', padding: '0.6rem', fontSize: 13, border: '1px solid #ccc', borderRadius: 5, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' },
//   criteriaActive: { marginTop: 6, fontSize: 13, color: '#2e7d32', fontWeight: 600 },
//   manualForm: { marginTop: '1rem', border: '1px dashed #bbb', padding: '1rem', borderRadius: 8, background: '#fff' },
//   manualGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
//   input: { width: '100%', padding: '0.6rem', fontSize: 14, border: '1px solid #ccc', borderRadius: 5, boxSizing: 'border-box' },
//   fileLabel: { display: 'block', fontSize: 13, marginBottom: 4, color: '#444' },
//   appCard: { marginBottom: '1rem', background: '#fff', padding: '0.75rem', borderRadius: 6, border: '1px solid #e8e8e8' },
//   appRow: { display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' },
//   smallText: { margin: '2px 0', fontSize: 13, color: '#555' },
//   fileLink: { fontSize: 13, color: '#1565c0', textDecoration: 'none' },
//   analysisBox: { marginTop: '0.75rem', backgroundColor: '#f5f5f5', padding: '0.75rem 1rem', borderRadius: 6, border: '1px solid #e0e0e0' },
//   analysisHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
//   analysisText: { whiteSpace: 'pre-wrap', margin: 0, fontSize: 13, lineHeight: 1.6, fontFamily: 'inherit' },
//   criteriaTag: { fontSize: 11, padding: '2px 7px', background: '#e8f5e9', color: '#2e7d32', borderRadius: 10, fontWeight: 600 },
//   badgeManual: { display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: '#fff3cd', color: '#856404', fontSize: 12 },
//   badgeApplicant: { display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: '#e8f5e9', color: '#2e7d32', fontSize: 12 },
//   btnPrimary: { padding: '0.6rem 1.2rem', fontSize: 14, border: 'none', borderRadius: 6, background: '#1565c0', color: '#fff', cursor: 'pointer' },
//   btnSecondary: { padding: '0.5rem 1rem', fontSize: 13, border: '1px solid #1565c0', borderRadius: 6, background: '#fff', color: '#1565c0', cursor: 'pointer' },
//   btnGreen: { padding: '0.6rem 1.2rem', fontSize: 14, border: 'none', borderRadius: 6, background: '#2e7d32', color: '#fff', cursor: 'pointer' },
//   btnAnalyze: { padding: '0.4rem 0.9rem', fontSize: 13, border: 'none', borderRadius: 5, background: '#0277bd', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' },
//   btnDelete: { padding: '0.4rem 0.9rem', fontSize: 13, border: 'none', borderRadius: 5, background: '#c62828', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' },
//   btnDisabled: { padding: '0.4rem 0.9rem', fontSize: 13, border: 'none', borderRadius: 6, background: '#bbb', color: '#fff', cursor: 'not-allowed' },
// };

// export default AdminJobApplicationsViewer;










// // src/components/AdminJobApplicationsViewer.js
// import React, { useEffect, useState } from 'react';
// import axios from 'axios';

// const AdminJobApplicationsViewer = ({ jobId, onClose }) => {
//   const [applications, setApplications] = useState([]);
//   const [analysisResults, setAnalysisResults] = useState({});
//   const [isAnalyzingAll, setIsAnalyzingAll] = useState(false);
//   const [rowLoading, setRowLoading] = useState({});
//   const [errorMsg, setErrorMsg] = useState(null);

//   // Custom criteria state
//   const [customCriteria, setCustomCriteria] = useState('');
//   const [showCriteriaPanel, setShowCriteriaPanel] = useState(false);

//   // Manual-entry UI state
//   const [showManualForm, setShowManualForm] = useState(false);
//   const [mName, setMName] = useState('');
//   const [mEmail, setMEmail] = useState('');
//   const [mPhone, setMPhone] = useState('');
//   const [mResume, setMResume] = useState(null);
//   const [mCover, setMCover] = useState(null);
//   const [manualMsg, setManualMsg] = useState('');

//   // ── helpers ──────────────────────────────────────────────────────────────
//   const idStr = (v) => (v == null ? '' : String(v));

//   const pickAnalysisText = (data, applicationId) => {
//     if (data?.results && applicationId) {
//       const byId = data.results[idStr(applicationId)];
//       if (typeof byId === 'string' && byId.trim()) return byId;
//     }
//     for (const key of ['analysis', 'message', 'result', 'text']) {
//       const val = data?.[key];
//       if (typeof val === 'string' && val.trim()) return val;
//     }
//     return '✅ Analysis completed.';
//   };

//   const fetchApplications = async () => {
//     setErrorMsg(null);
//     try {
//       const res = await axios.get(
//         `${process.env.REACT_APP_API}/admin/job/application/${jobId}`,
//         { withCredentials: true }
//       );
//       setApplications(res.data.applications || []);
//     } catch (err) {
//       console.error('[AdminViewer] Fetch failed:', err);
//       setErrorMsg('Failed to fetch applications.');
//     }
//   };

//   useEffect(() => {
//     if (jobId) fetchApplications();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [jobId]);

//   // ── Bulk Analysis ─────────────────────────────────────────────────────────
//   const analyzeAllApplications = async () => {
//     setIsAnalyzingAll(true);
//     setErrorMsg(null);
//     try {
//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/application/analyze/job/${jobId}`,
//         { customCriteria },         // ← sends custom criteria to backend
//         { withCredentials: true }
//       );
//       const results = res.data?.results || {};
//       const normalized = Object.fromEntries(
//         Object.entries(results).map(([k, v]) => [idStr(k), v])
//       );
//       setAnalysisResults(normalized);

//       const { successCount, failCount, elapsedMs } = res.data;
//       if (failCount > 0) {
//         setErrorMsg(`Bulk analysis complete — ${successCount} succeeded, ${failCount} failed. Check individual results below.`);
//       }
//       console.log(`[Bulk] Done — ${successCount} ok, ${failCount} failed, ${elapsedMs}ms`);
//     } catch (err) {
//       console.error('[AI Bulk Analysis] Failed:', err);
//       const status = err?.response?.status;
//       if (status === 504) {
//         setErrorMsg('Bulk analysis timed out — the server is taking too long. Try analyzing applications one at a time, or contact your admin to increase the server timeout.');
//       } else {
//         setErrorMsg(`Bulk analysis failed (HTTP ${status || 'network error'}). Please try again.`);
//       }
//     } finally {
//       setIsAnalyzingAll(false);
//     }
//   };

//   // ── Single Analysis ───────────────────────────────────────────────────────
//   const analyzeSingleApplication = async (applicationId) => {
//     const key = idStr(applicationId);
//     setRowLoading(prev => ({ ...prev, [key]: true }));
//     setErrorMsg(null);

//     try {
//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/application/analyze/${key}`,
//         { customCriteria },         // ← sends custom criteria to backend
//         { withCredentials: true }
//       );
//       const text = pickAnalysisText(res.data, key);
//       setAnalysisResults(prev => ({ ...prev, [key]: text }));
//     } catch (err) {
//       console.error(`[AI Single Analysis] Failed for ${key}:`, err);
//       const status = err?.response?.status;
//       setAnalysisResults(prev => ({
//         ...prev,
//         [key]: `❌ Error analyzing application (HTTP ${status || 'network error'}).`,
//       }));
//     } finally {
//       setRowLoading(prev => ({ ...prev, [key]: false }));
//     }
//   };

//   // ── Manual Candidate Submit ───────────────────────────────────────────────
//   const submitManualCandidate = async (e) => {
//     e.preventDefault();
//     setManualMsg('');
//     if (!mName || !mEmail || !mPhone || !mResume || !mCover) {
//       setManualMsg('Please complete all fields and attach both files.');
//       return;
//     }
//     try {
//       const fd = new FormData();
//       fd.append('jobId', jobId);
//       fd.append('name', mName);
//       fd.append('email', mEmail);
//       fd.append('phone', mPhone);
//       fd.append('resume', mResume);
//       fd.append('coverLetter', mCover);
//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/manual-entry`,
//         fd,
//         { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
//       );
//       if (res.data?.success) {
//         setManualMsg('✅ Candidate added.');
//         await fetchApplications();
//         setMName(''); setMEmail(''); setMPhone('');
//         setMResume(null); setMCover(null);
//         setShowManualForm(false);
//       } else {
//         setManualMsg('❌ Failed to add candidate.');
//       }
//     } catch (err) {
//       console.error('[Manual Entry] Failed:', err);
//       setManualMsg('❌ Error adding candidate.');
//     }
//   };

//   // ── Render ────────────────────────────────────────────────────────────────
//   return (
//     <div style={styles.container}>
//       <div style={styles.header}>
//         <h3 style={{ margin: 0 }}>Applications for Job ID: {jobId}</h3>
//         <button onClick={onClose} style={styles.closeBtn}>✕ Close</button>
//       </div>

//       {errorMsg && (
//         <div style={styles.errorBanner}>{errorMsg}</div>
//       )}

//       {/* ── Custom Criteria Panel ── */}
//       <div style={styles.criteriaSection}>
//         <button
//           onClick={() => setShowCriteriaPanel(s => !s)}
//           style={styles.btnSecondary}
//         >
//           {showCriteriaPanel ? '▲ Hide' : '▼ Set'} Custom Evaluation Criteria
//         </button>
//         {showCriteriaPanel && (
//           <div style={styles.criteriaPanel}>
//             <p style={styles.criteriaHint}>
//               Enter specific skills, certifications, or experience items you want the AI to prioritize.
//               Leave blank to use the default CPA firm / government auditing criteria.
//             </p>
//             <textarea
//               value={customCriteria}
//               onChange={e => setCustomCriteria(e.target.value)}
//               placeholder={
//                 'Examples:\n' +
//                 '• Must have active CPA license\n' +
//                 '• DoD Secret clearance required\n' +
//                 '• Experience with SAP or Deltek\n' +
//                 '• GAAP and GAAS familiarity\n' +
//                 '• 3+ years federal government audit'
//               }
//               rows={6}
//               style={styles.criteriaTextarea}
//             />
//             {customCriteria.trim() && (
//               <div style={styles.criteriaActive}>
//                 ✅ Custom criteria active — will be applied to all analyses below
//               </div>
//             )}
//           </div>
//         )}
//       </div>

//       {applications.length === 0 ? (
//         <p>No applications submitted yet.</p>
//       ) : (
//         <>
//           <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '1rem 0' }}>
//             <button
//               onClick={analyzeAllApplications}
//               disabled={isAnalyzingAll}
//               style={isAnalyzingAll ? styles.btnDisabled : styles.btnPrimary}
//             >
//               {isAnalyzingAll
//                 ? `⏳ Analyzing ${applications.length} applications…`
//                 : `🤖 Analyze All Applications (${applications.length})`}
//             </button>

//             <button
//               onClick={() => setShowManualForm(s => !s)}
//               style={styles.btnGreen}
//             >
//               {showManualForm ? '✕ Cancel Manual Add' : '+ Add Candidate Manually'}
//             </button>
//           </div>

//           {/* ── Manual Entry Form ── */}
//           {showManualForm && (
//             <form onSubmit={submitManualCandidate} style={styles.manualForm}>
//               <div style={styles.manualGrid}>
//                 <input type="text" placeholder="Full Name" value={mName} onChange={e => setMName(e.target.value)} required style={styles.input} />
//                 <input type="email" placeholder="Email" value={mEmail} onChange={e => setMEmail(e.target.value)} required style={styles.input} />
//                 <input type="tel" placeholder="Phone" value={mPhone} onChange={e => setMPhone(e.target.value)} required style={styles.input} />
//                 <div />
//                 <div>
//                   <label style={styles.fileLabel}>Resume (PDF/DOC/DOCX)</label>
//                   <input type="file" accept=".pdf,.doc,.docx" onChange={e => setMResume(e.target.files[0] || null)} required />
//                 </div>
//                 <div>
//                   <label style={styles.fileLabel}>Cover Letter (PDF/DOC/DOCX)</label>
//                   <input type="file" accept=".pdf,.doc,.docx" onChange={e => setMCover(e.target.files[0] || null)} required />
//                 </div>
//               </div>
//               <button type="submit" style={{ ...styles.btnPrimary, marginTop: 12 }}>Add Candidate</button>
//               {manualMsg && (
//                 <div style={{ marginTop: 8, color: manualMsg.startsWith('✅') ? '#2e7d32' : '#b00020' }}>
//                   {manualMsg}
//                 </div>
//               )}
//             </form>
//           )}

//           {/* ── Application List ── */}
//           <ul style={{ listStyle: 'none', padding: 0, marginTop: '1rem' }}>
//             {applications.map(app => {
//               const appId = idStr(app._id);
//               const analysis = analysisResults[appId];
//               const isLoading = !!rowLoading[appId];

//               return (
//                 <li key={appId} style={styles.appCard}>
//                   <div style={styles.appRow}>
//                     {/* Applicant info */}
//                     <div style={{ flex: 1 }}>
//                       <p style={{ margin: '2px 0' }}><strong>{app.name}</strong></p>
//                       <p style={styles.smallText}>{app.email} | {app.phone}</p>
//                       <span style={app.isManualEntry ? styles.badgeManual : styles.badgeApplicant}>
//                         {app.isManualEntry ? 'Manually Added' : 'Applicant'}
//                       </span>
//                     </div>

//                     {/* File links */}
//                     <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
//                       <a
//                         href={`${process.env.REACT_APP_API}/uploads/jobApplications/${app.resumeFile}`}
//                         target="_blank" rel="noreferrer"
//                         style={styles.fileLink}
//                       >
//                         📄 Resume
//                       </a>
//                       <a
//                         href={`${process.env.REACT_APP_API}/uploads/jobApplications/${app.coverLetterFile}`}
//                         target="_blank" rel="noreferrer"
//                         style={styles.fileLink}
//                       >
//                         📝 Cover
//                       </a>
//                     </div>

//                     {/* Analyze button */}
//                     <button
//                       onClick={() => analyzeSingleApplication(appId)}
//                       disabled={isLoading}
//                       style={isLoading ? styles.btnDisabled : styles.btnAnalyze}
//                     >
//                       {isLoading ? '⏳ Analyzing…' : '🔍 Analyze'}
//                     </button>
//                   </div>

//                   {/* AI Result */}
//                   {analysis && (
//                     <div style={styles.analysisBox}>
//                       <div style={styles.analysisHeader}>
//                         <strong>🤖 AI Analysis</strong>
//                         {customCriteria.trim() && (
//                           <span style={styles.criteriaTag}>Custom criteria applied</span>
//                         )}
//                       </div>
//                       <pre style={styles.analysisText}>{analysis}</pre>
//                     </div>
//                   )}
//                   <hr style={{ borderColor: '#e0e0e0', margin: '0.75rem 0 0' }} />
//                 </li>
//               );
//             })}
//           </ul>
//         </>
//       )}
//     </div>
//   );
// };

// // ── Styles ────────────────────────────────────────────────────────────────────
// const styles = {
//   container: { marginTop: '1.5rem', border: '1px solid #ddd', padding: '1.25rem', borderRadius: 8, background: '#fafafa' },
//   header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
//   closeBtn: { padding: '4px 12px', border: '1px solid #ccc', borderRadius: 5, cursor: 'pointer', background: '#fff' },
//   errorBanner: { margin: '0.5rem 0', padding: '0.5rem 0.75rem', background: '#fdecea', color: '#b71c1c', borderRadius: 6, fontSize: 14 },
//   criteriaSection: { marginBottom: '1rem' },
//   criteriaPanel: { marginTop: 8, padding: '1rem', background: '#fff', border: '1px solid #ddd', borderRadius: 6 },
//   criteriaHint: { margin: '0 0 8px', fontSize: 13, color: '#555' },
//   criteriaTextarea: { width: '100%', padding: '0.6rem', fontSize: 13, border: '1px solid #ccc', borderRadius: 5, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' },
//   criteriaActive: { marginTop: 6, fontSize: 13, color: '#2e7d32', fontWeight: 600 },
//   manualForm: { marginTop: '1rem', border: '1px dashed #bbb', padding: '1rem', borderRadius: 8, background: '#fff' },
//   manualGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
//   input: { width: '100%', padding: '0.6rem', fontSize: 14, border: '1px solid #ccc', borderRadius: 5, boxSizing: 'border-box' },
//   fileLabel: { display: 'block', fontSize: 13, marginBottom: 4, color: '#444' },
//   appCard: { marginBottom: '1rem', background: '#fff', padding: '0.75rem', borderRadius: 6, border: '1px solid #e8e8e8' },
//   appRow: { display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' },
//   smallText: { margin: '2px 0', fontSize: 13, color: '#555' },
//   fileLink: { fontSize: 13, color: '#1565c0', textDecoration: 'none' },
//   analysisBox: { marginTop: '0.75rem', backgroundColor: '#f5f5f5', padding: '0.75rem 1rem', borderRadius: 6, border: '1px solid #e0e0e0' },
//   analysisHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
//   analysisText: { whiteSpace: 'pre-wrap', margin: 0, fontSize: 13, lineHeight: 1.6, fontFamily: 'inherit' },
//   criteriaTag: { fontSize: 11, padding: '2px 7px', background: '#e8f5e9', color: '#2e7d32', borderRadius: 10, fontWeight: 600 },
//   badgeManual: { display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: '#fff3cd', color: '#856404', fontSize: 12 },
//   badgeApplicant: { display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: '#e8f5e9', color: '#2e7d32', fontSize: 12 },
//   btnPrimary: { padding: '0.6rem 1.2rem', fontSize: 14, border: 'none', borderRadius: 6, background: '#1565c0', color: '#fff', cursor: 'pointer' },
//   btnSecondary: { padding: '0.5rem 1rem', fontSize: 13, border: '1px solid #1565c0', borderRadius: 6, background: '#fff', color: '#1565c0', cursor: 'pointer' },
//   btnGreen: { padding: '0.6rem 1.2rem', fontSize: 14, border: 'none', borderRadius: 6, background: '#2e7d32', color: '#fff', cursor: 'pointer' },
//   btnAnalyze: { padding: '0.4rem 0.9rem', fontSize: 13, border: 'none', borderRadius: 5, background: '#0277bd', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' },
//   btnDisabled: { padding: '0.6rem 1.2rem', fontSize: 14, border: 'none', borderRadius: 6, background: '#bbb', color: '#fff', cursor: 'not-allowed' },
// };

// export default AdminJobApplicationsViewer;










// // src/components/AdminJobApplicationsViewer.js
// import React, { useEffect, useState } from 'react';
// import axios from 'axios';

// const AdminJobApplicationsViewer = ({ jobId, onClose }) => {
//   const [applications, setApplications] = useState([]);
//   const [analysisResults, setAnalysisResults] = useState({}); // { [appId]: text }
//   const [isAnalyzingAll, setIsAnalyzingAll] = useState(false);
//   const [rowLoading, setRowLoading] = useState({}); // { [appId]: boolean }
//   const [errorMsg, setErrorMsg] = useState(null);

//   // Manual-entry UI state
//   const [showManualForm, setShowManualForm] = useState(false);
//   const [mName, setMName] = useState('');
//   const [mEmail, setMEmail] = useState('');
//   const [mPhone, setMPhone] = useState('');
//   const [mResume, setMResume] = useState(null);
//   const [mCover, setMCover] = useState(null);
//   const [manualMsg, setManualMsg] = useState('');

//   // ---- helpers ----
//   const idStr = (v) => (v == null ? '' : String(v));
//   const pickAnalysisText = (data, applicationId) => {
//     // Handle bulk-style object: { results: { [id]: text } }
//     if (data?.results && applicationId) {
//       const byId = data.results[idStr(applicationId)];
//       if (typeof byId === 'string' && byId.trim()) return byId;
//     }
//     // Common single keys we use in different controllers
//     for (const key of ['analysis', 'message', 'result', 'text']) {
//       const val = data?.[key];
//       if (typeof val === 'string' && val.trim()) return val;
//     }
//     return '✅ Analysis completed.'; // fallback
//   };

//   const fetchApplications = async () => {
//     setErrorMsg(null);
//     try {
//       const res = await axios.get(
//         `${process.env.REACT_APP_API}/admin/job/application/${jobId}`,
//         { withCredentials: true }
//       );
//       setApplications(res.data.applications || []);
//     } catch (err) {
//       console.error('[AdminViewer] Fetch failed:', err);
//       setErrorMsg('Failed to fetch applications.');
//     }
//   };

//   useEffect(() => {
//     if (jobId) fetchApplications();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [jobId]);

//   // -------- Bulk Analysis (group) --------
//   const analyzeAllApplications = async () => {
//     setIsAnalyzingAll(true);
//     setErrorMsg(null);
//     try {
//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/application/analyze/job/${jobId}`,
//         {},
//         { withCredentials: true }
//       );
//       // API returns { success, results: { [appId]: string } }
//       const results = res.data?.results || {};
//       // Normalize keys to strings
//       const normalized = Object.fromEntries(
//         Object.entries(results).map(([k, v]) => [idStr(k), v])
//       );
//       setAnalysisResults(normalized);
//     } catch (err) {
//       console.error('[AI Bulk Analysis] Failed:', err);
//       setErrorMsg('Bulk analysis failed. Please try again.');
//     } finally {
//       setIsAnalyzingAll(false);
//     }
//   };

//   // -------- Single Application Analysis --------
//   const analyzeSingleApplication = async (applicationId) => {
//     const key = idStr(applicationId);
//     setRowLoading(prev => ({ ...prev, [key]: true }));
//     setErrorMsg(null);

//     try {
//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/application/analyze/${key}`,
//         {},
//         { withCredentials: true }
//       );

//       const text = pickAnalysisText(res.data, key);

//       setAnalysisResults(prev => ({
//         ...prev,
//         [key]: text,
//       }));
//     } catch (err) {
//       console.error(`[AI Single Analysis] Failed for ${key}:`, err);
//       setAnalysisResults(prev => ({
//         ...prev,
//         [key]: '❌ Error analyzing application.',
//       }));
//     } finally {
//       setRowLoading(prev => ({ ...prev, [key]: false }));
//     }
//   };

//   // -------- Manual Candidate Submit --------
//   const submitManualCandidate = async (e) => {
//     e.preventDefault();
//     setManualMsg('');

//     if (!mName || !mEmail || !mPhone || !mResume || !mCover) {
//       setManualMsg('Please complete all fields and attach both files.');
//       return;
//     }

//     try {
//       const fd = new FormData();
//       fd.append('jobId', jobId);
//       fd.append('name', mName);
//       fd.append('email', mEmail);
//       fd.append('phone', mPhone);
//       fd.append('resume', mResume);
//       fd.append('coverLetter', mCover);

//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/manual-entry`,
//         fd,
//         {
//           headers: { 'Content-Type': 'multipart/form-data' },
//           withCredentials: true
//         }
//       );

//       if (res.data?.success) {
//         setManualMsg('✅ Candidate added.');
//         await fetchApplications();
//         setMName(''); setMEmail(''); setMPhone('');
//         setMResume(null); setMCover(null);
//         setShowManualForm(false);
//       } else {
//         setManualMsg('❌ Failed to add candidate.');
//       }
//     } catch (err) {
//       console.error('[Manual Entry] Failed:', err);
//       setManualMsg('❌ Error adding candidate.');
//     }
//   };

//   return (
//     <div style={{ marginTop: '1.5rem', border: '1px solid #ccc', padding: '1rem', borderRadius: '8px' }}>
//       <div style={{ display: 'flex', justifyContent: 'space-between' }}>
//         <h3>Applications for Job ID: {jobId}</h3>
//         <button onClick={onClose}>Close</button>
//       </div>

//       {errorMsg && (
//         <div style={{ margin: '0.5rem 0', padding: '0.5rem 0.75rem', background: '#fdecea', color: '#b71c1c', borderRadius: 6 }}>
//           {errorMsg}
//         </div>
//       )}

//       {applications.length === 0 ? (
//         <p>No applications submitted yet.</p>
//       ) : (
//         <>
//           <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
//             <button onClick={analyzeAllApplications} disabled={isAnalyzingAll}>
//               {isAnalyzingAll ? 'Analyzing all…' : 'Analyze All Applications'}
//             </button>

//             <button onClick={() => setShowManualForm((s) => !s)}>
//               {showManualForm ? 'Cancel Manual Add' : 'Add Candidate Manually'}
//             </button>
//           </div>

//           {showManualForm && (
//             <form onSubmit={submitManualCandidate} style={{ marginTop: '1rem', border: '1px dashed #bbb', padding: '1rem', borderRadius: 8 }}>
//               <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
//                 <input type="text" placeholder="Full Name" value={mName} onChange={e => setMName(e.target.value)} required />
//                 <input type="email" placeholder="Email" value={mEmail} onChange={e => setMEmail(e.target.value)} required />
//                 <input type="tel" placeholder="Phone" value={mPhone} onChange={e => setMPhone(e.target.value)} required />
//                 <div />
//                 <div>
//                   <label>Resume (PDF/DOC/DOCX)</label>
//                   <input type="file" accept=".pdf,.doc,.docx" onChange={e => setMResume(e.target.files[0] || null)} required />
//                 </div>
//                 <div>
//                   <label>Cover Letter (PDF/DOC/DOCX)</label>
//                   <input type="file" accept=".pdf,.doc,.docx" onChange={e => setMCover(e.target.files[0] || null)} required />
//                 </div>
//               </div>

//               <div style={{ marginTop: 12 }}>
//                 <button type="submit">Add Candidate</button>
//               </div>
//               {manualMsg && (
//                 <div style={{ marginTop: 8, color: manualMsg.startsWith('✅') ? '#2e7d32' : '#b00020' }}>
//                   {manualMsg}
//                 </div>
//               )}
//             </form>
//           )}

//           <ul style={{ marginTop: '1.5rem' }}>
//             {applications.map(app => {
//               const appId = idStr(app._id);
//               const analysis = analysisResults[appId];

//               return (
//                 <li key={appId} style={{ marginBottom: '1.5rem' }}>
//                   <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
//                     <div>
//                       <p><strong>Name:</strong> {app.name}</p>
//                       <p><strong>Email:</strong> {app.email}</p>
//                       <p><strong>Phone:</strong> {app.phone}</p>
//                       {app.isManualEntry ? (
//                         <span style={{ padding: '2px 8px', borderRadius: 6, background: '#fff3cd', color: '#856404' }}>
//                           Manually Added
//                         </span>
//                       ) : (
//                         <span style={{ padding: '2px 8px', borderRadius: 6, background: '#e8f5e9', color: '#2e7d32' }}>
//                           Applicant
//                         </span>
//                       )}
//                     </div>

//                     <div>
//                       <a
//                         href={`${process.env.REACT_APP_API}/uploads/jobApplications/${app.resumeFile}`}
//                         target="_blank" rel="noreferrer"
//                       >
//                         📄 Resume
//                       </a>{' '}|{' '}
//                       <a
//                         href={`${process.env.REACT_APP_API}/uploads/jobApplications/${app.coverLetterFile}`}
//                         target="_blank" rel="noreferrer"
//                       >
//                         📝 Cover Letter
//                       </a>
//                     </div>

//                     <div>
//                       <button
//                         onClick={() => analyzeSingleApplication(appId)}
//                         disabled={!!rowLoading[appId]}
//                       >
//                         {rowLoading[appId] ? 'Analyzing…' : 'Analyze This Application'}
//                       </button>
//                     </div>
//                   </div>

//                   {analysis && (
//                     <div style={{ marginTop: '0.5rem', backgroundColor: '#f8f8f8', padding: '1rem', borderRadius: 6 }}>
//                       <strong>AI Analysis:</strong>
//                       <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{analysis}</pre>
//                     </div>
//                   )}
//                   <hr />
//                 </li>
//               );
//             })}
//           </ul>
//         </>
//       )}
//     </div>
//   );
// };

// export default AdminJobApplicationsViewer;






// // src/components/AdminJobApplicationsViewer.js
// import React, { useEffect, useState } from 'react';
// import axios from 'axios';

// const AdminJobApplicationsViewer = ({ jobId, onClose }) => {
//   const [applications, setApplications] = useState([]);
//   const [analysisResults, setAnalysisResults] = useState({}); // { [applicationId]: text }
//   const [isAnalyzingAll, setIsAnalyzingAll] = useState(false);
//   const [rowLoading, setRowLoading] = useState({}); // { [applicationId]: boolean }

//   // Manual-entry UI state
//   const [showManualForm, setShowManualForm] = useState(false);
//   const [mName, setMName] = useState('');
//   const [mEmail, setMEmail] = useState('');
//   const [mPhone, setMPhone] = useState('');
//   const [mResume, setMResume] = useState(null);
//   const [mCover, setMCover] = useState(null);
//   const [manualMsg, setManualMsg] = useState('');

//   useEffect(() => {
//     const fetchApplications = async () => {
//       try {
//         const res = await axios.get(
//           `${process.env.REACT_APP_API}/admin/job/application/${jobId}`,
//           { withCredentials: true }
//         );
//         setApplications(res.data.applications || []);
//       } catch (err) {
//         console.error('[AdminViewer] Fetch failed:', err);
//       }
//     };

//     if (jobId) fetchApplications();
//   }, [jobId]);

//   // -------- Bulk Analysis (group) --------
//   const analyzeAllApplications = async () => {
//     setIsAnalyzingAll(true);
//     try {
//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/application/analyze/job/${jobId}`,
//         {},
//         { withCredentials: true }
//       );

//       // API returns { success: true, results: { [applicationId]: "text" } }
//       setAnalysisResults(res.data.results || {});
//     } catch (err) {
//       console.error('[AI Bulk Analysis] Failed:', err);
//     } finally {
//       setIsAnalyzingAll(false);
//     }
//   };

//   // -------- Single Application Analysis --------
//   const analyzeSingleApplication = async (applicationId) => {
//     setRowLoading(prev => ({ ...prev, [applicationId]: true }));
//     try {
//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/application/analyze/${applicationId}`,
//         {},
//         { withCredentials: true }
//       );

//       // API should return { success: true, message: "AI feedback text" }
//       if (res.data && res.data.success) {
//         setAnalysisResults(prev => ({
//           ...prev,
//           [applicationId]: res.data.message || '✅ Analysis completed.'
//         }));
//       } else {
//         setAnalysisResults(prev => ({
//           ...prev,
//           [applicationId]: '❌ Analysis failed or returned no results.'
//         }));
//       }
//     } catch (err) {
//       console.error(`[AI Single Analysis] Failed for ${applicationId}:`, err);
//       setAnalysisResults(prev => ({
//         ...prev,
//         [applicationId]: '❌ Error analyzing application.'
//       }));
//     } finally {
//       setRowLoading(prev => ({ ...prev, [applicationId]: false }));
//     }
//   };

//   // -------- Manual Candidate Submit --------
//   const submitManualCandidate = async (e) => {
//     e.preventDefault();
//     setManualMsg('');

//     if (!mName || !mEmail || !mPhone || !mResume || !mCover) {
//       setManualMsg('Please complete all fields and attach both files.');
//       return;
//     }

//     try {
//       const fd = new FormData();
//       fd.append('jobId', jobId);
//       fd.append('name', mName);
//       fd.append('email', mEmail);
//       fd.append('phone', mPhone);
//       fd.append('resume', mResume);
//       fd.append('coverLetter', mCover);

//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/manual-entry`,
//         fd,
//         {
//           headers: { 'Content-Type': 'multipart/form-data' },
//           withCredentials: true
//         }
//       );

//       if (res.data && res.data.success) {
//         setManualMsg('✅ Candidate added.');
//         // Refresh list
//         const refreshed = await axios.get(
//           `${process.env.REACT_APP_API}/admin/job/application/${jobId}`,
//           { withCredentials: true }
//         );
//         setApplications(refreshed.data.applications || []);
//         // Clear form
//         setMName(''); setMEmail(''); setMPhone('');
//         setMResume(null); setMCover(null);
//         setShowManualForm(false);
//       } else {
//         setManualMsg('❌ Failed to add candidate.');
//       }
//     } catch (err) {
//       console.error('[Manual Entry] Failed:', err);
//       setManualMsg('❌ Error adding candidate.');
//     }
//   };

//   return (
//     <div style={{ marginTop: '1.5rem', border: '1px solid #ccc', padding: '1rem', borderRadius: '8px' }}>
//       <h3>Applications for Job ID: {jobId}</h3>
//       <button onClick={onClose} style={{ float: 'right' }}>Close</button>
//       <br /><br />

//       {applications.length === 0 ? (
//         <p>No applications submitted yet.</p>
//       ) : (
//         <>
//           <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
//             <button onClick={analyzeAllApplications} disabled={isAnalyzingAll}>
//               {isAnalyzingAll ? 'Analyzing all…' : 'Analyze All Applications'}
//             </button>

//             <button onClick={() => setShowManualForm((s) => !s)}>
//               {showManualForm ? 'Cancel Manual Add' : 'Add Candidate Manually'}
//             </button>
//           </div>

//           {/* Manual Entry Form */}
//           {showManualForm && (
//             <form onSubmit={submitManualCandidate} style={{ marginTop: '1rem', border: '1px dashed #bbb', padding: '1rem', borderRadius: 8 }}>
//               <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
//                 <input
//                   type="text"
//                   placeholder="Full Name"
//                   value={mName}
//                   onChange={e => setMName(e.target.value)}
//                   required
//                 />
//                 <input
//                   type="email"
//                   placeholder="Email"
//                   value={mEmail}
//                   onChange={e => setMEmail(e.target.value)}
//                   required
//                 />
//                 <input
//                   type="tel"
//                   placeholder="Phone"
//                   value={mPhone}
//                   onChange={e => setMPhone(e.target.value)}
//                   required
//                 />
//                 <div />
//                 <div>
//                   <label>Resume (PDF/DOC/DOCX)</label>
//                   <input
//                     type="file"
//                     accept=".pdf,.doc,.docx"
//                     onChange={e => setMResume(e.target.files[0] || null)}
//                     required
//                   />
//                 </div>
//                 <div>
//                   <label>Cover Letter (PDF/DOC/DOCX)</label>
//                   <input
//                     type="file"
//                     accept=".pdf,.doc,.docx"
//                     onChange={e => setMCover(e.target.files[0] || null)}
//                     required
//                   />
//                 </div>
//               </div>

//               <div style={{ marginTop: 12 }}>
//                 <button type="submit">Add Candidate</button>
//               </div>
//               {manualMsg && (
//                 <div style={{ marginTop: 8, color: manualMsg.startsWith('✅') ? '#2e7d32' : '#b00020' }}>
//                   {manualMsg}
//                 </div>
//               )}
//             </form>
//           )}

//           {/* Applications List */}
//           <ul style={{ marginTop: '1.5rem' }}>
//             {applications.map(app => (
//               <li key={app._id} style={{ marginBottom: '1.5rem' }}>
//                 <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
//                   <div>
//                     <p><strong>Name:</strong> {app.name}</p>
//                     <p><strong>Email:</strong> {app.email}</p>
//                     <p><strong>Phone:</strong> {app.phone}</p>
//                     {app.isManualEntry ? (
//                       <span style={{ padding: '2px 8px', borderRadius: 6, background: '#fff3cd', color: '#856404' }}>
//                         Manually Added
//                       </span>
//                     ) : (
//                       <span style={{ padding: '2px 8px', borderRadius: 6, background: '#e8f5e9', color: '#2e7d32' }}>
//                         Applicant
//                       </span>
//                     )}
//                   </div>

//                   <div>
//                     <a
//                       href={`${process.env.REACT_APP_API}/uploads/jobApplications/${app.resumeFile}`}
//                       target="_blank" rel="noreferrer"
//                     >
//                       📄 Resume
//                     </a>{' '}&nbsp;|&nbsp;{' '}
//                     <a
//                       href={`${process.env.REACT_APP_API}/uploads/jobApplications/${app.coverLetterFile}`}
//                       target="_blank" rel="noreferrer"
//                     >
//                       📝 Cover Letter
//                     </a>
//                   </div>

//                   <div>
//                     <button
//                       onClick={() => analyzeSingleApplication(app._id)}
//                       disabled={!!rowLoading[app._id]}
//                     >
//                       {rowLoading[app._id] ? 'Analyzing…' : 'Analyze This Application'}
//                     </button>
//                   </div>
//                 </div>

//                 {analysisResults[app._id] && (
//                   <div style={{ marginTop: '0.5rem', backgroundColor: '#f8f8f8', padding: '1rem', borderRadius: 6 }}>
//                     <strong>AI Analysis:</strong>
//                     <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{analysisResults[app._id]}</pre>
//                   </div>
//                 )}
//                 <hr />
//               </li>
//             ))}
//           </ul>
//         </>
//       )}
//     </div>
//   );
// };

// export default AdminJobApplicationsViewer;










// // src/components/AdminJobApplicationsViewer.js
// import React, { useEffect, useState } from 'react';
// import axios from 'axios';
// import AdminManualApplicationForm from './AdminManualApplicationForm';

// const AdminJobApplicationsViewer = ({ jobId, onClose }) => {
//   const [applications, setApplications] = useState([]);
//   const [analysisResults, setAnalysisResults] = useState({});
//   const [isAnalyzingAll, setIsAnalyzingAll] = useState(false);
//   const [rowLoading, setRowLoading] = useState({});
//   const [showManualForm, setShowManualForm] = useState(false);

//   const fetchApplications = async () => {
//     try {
//       const res = await axios.get(
//         `${process.env.REACT_APP_API}/admin/job/application/${jobId}`,
//         { withCredentials: true }
//       );
//       setApplications(res.data.applications || []);
//     } catch (err) {
//       console.error('[AdminViewer] Fetch failed:', err);
//     }
//   };

//   useEffect(() => {
//     if (jobId) fetchApplications();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [jobId]);

//   const analyzeAllApplications = async () => {
//     setIsAnalyzingAll(true);
//     try {
//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/application/analyze/job/${jobId}`,
//         {},
//         { withCredentials: true }
//       );
//       setAnalysisResults(res.data.results || {});
//     } catch (err) {
//       console.error('[AI Bulk Analysis] Failed:', err);
//     } finally {
//       setIsAnalyzingAll(false);
//     }
//   };

//   const analyzeSingleApplication = async (applicationId) => {
//     setRowLoading(prev => ({ ...prev, [applicationId]: true }));
//     try {
//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/application/analyze/${applicationId}`,
//         {},
//         { withCredentials: true }
//       );
//       setAnalysisResults(prev => ({
//         ...prev,
//         [applicationId]: res.data.message || '✅ Analysis completed.'
//       }));
//     } catch (err) {
//       console.error(`[AI Single Analysis] Failed for ${applicationId}:`, err);
//       setAnalysisResults(prev => ({
//         ...prev,
//         [applicationId]: '❌ Error analyzing application.'
//       }));
//     } finally {
//       setRowLoading(prev => ({ ...prev, [applicationId]: false }));
//     }
//   };

//   return (
//     <div style={{ marginTop: '1.5rem', border: '1px solid #ccc', padding: '1rem', borderRadius: '8px' }}>
//       <h3>Applications for Job ID: {jobId}</h3>
//       <button onClick={onClose} style={{ float: 'right' }}>Close</button>
//       <br /><br />

//       {applications.length === 0 ? (
//         <p>No applications submitted yet.</p>
//       ) : (
//         <>
//           <div style={{ display:'flex', gap: 12, flexWrap: 'wrap' }}>
//             <button onClick={analyzeAllApplications} disabled={isAnalyzingAll}>
//               {isAnalyzingAll ? 'Analyzing all…' : 'Analyze All Applications'}
//             </button>
//             <button onClick={() => setShowManualForm(v => !v)}>
//               {showManualForm ? 'Hide Manual Add' : 'Add Candidate Manually'}
//             </button>
//           </div>

//           {showManualForm && (
//             <AdminManualApplicationForm
//               jobId={jobId}
//               onCreated={() => fetchApplications()}
//             />
//           )}

//           <ul style={{ marginTop: '1.5rem' }}>
//             {applications.map(app => (
//               <li key={app._id} style={{ marginBottom: '1.5rem' }}>
//                 <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
//                   <div>
//                     <p><strong>Name:</strong> {app.name}</p>
//                     <p><strong>Email:</strong> {app.email}</p>
//                     <p><strong>Phone:</strong> {app.phone}</p>
//                     {app.isManualEntry ? (
//                       <span style={{ padding: '2px 8px', borderRadius: 6, background: '#fff3cd', color: '#856404' }}>
//                         Manually Added
//                       </span>
//                     ) : (
//                       <span style={{ padding: '2px 8px', borderRadius: 6, background: '#e8f5e9', color: '#2e7d32' }}>
//                         Applicant
//                       </span>
//                     )}
//                   </div>

//                   <div>
//                     <a
//                       href={`${process.env.REACT_APP_API}/uploads/jobApplications/${app.resumeFile}`}
//                       target="_blank" rel="noreferrer"
//                     >
//                       📄 Resume
//                     </a>{' '}&nbsp;|&nbsp;{' '}
//                     <a
//                       href={`${process.env.REACT_APP_API}/uploads/jobApplications/${app.coverLetterFile}`}
//                       target="_blank" rel="noreferrer"
//                     >
//                       📝 Cover Letter
//                     </a>
//                   </div>

//                   <div>
//                     <button
//                       onClick={() => analyzeSingleApplication(app._id)}
//                       disabled={!!rowLoading[app._id]}
//                     >
//                       {rowLoading[app._id] ? 'Analyzing…' : 'Analyze This Application'}
//                     </button>
//                   </div>
//                 </div>

//                 {analysisResults[app._id] && (
//                   <div style={{ marginTop: '0.5rem', backgroundColor: '#f8f8f8', padding: '1rem' }}>
//                     <strong>AI Analysis:</strong>
//                     <pre style={{ whiteSpace: 'pre-wrap' }}>{analysisResults[app._id]}</pre>
//                   </div>
//                 )}
//                 <hr />
//               </li>
//             ))}
//           </ul>
//         </>
//       )}
//     </div>
//   );
// };

// export default AdminJobApplicationsViewer;








// // src/components/AdminJobApplicationsViewer.js
// import React, { useEffect, useState } from 'react';
// import axios from 'axios';

// const AdminJobApplicationsViewer = ({ jobId, onClose }) => {
//   const [applications, setApplications] = useState([]);
//   const [analysisResults, setAnalysisResults] = useState({});
//   const [isAnalyzingAll, setIsAnalyzingAll] = useState(false);
//   const [rowLoading, setRowLoading] = useState({}); // { [appId]: boolean }

//   // manual-add form
//   const [showManualForm, setShowManualForm] = useState(false);
//   const [mName, setMName] = useState('');
//   const [mEmail, setMEmail] = useState('');
//   const [mPhone, setMPhone] = useState('');
//   const [mResume, setMResume] = useState(null);
//   const [mCover, setMCover] = useState(null);
//   const [manualMsg, setManualMsg] = useState('');

//   const fetchApplications = async () => {
//     try {
//       const res = await axios.get(
//         `${process.env.REACT_APP_API}/admin/job/application/${jobId}`,
//         { withCredentials: true }
//       );
//       setApplications(res.data.applications || []);
//     } catch (err) {
//       console.error('[AdminViewer] Fetch failed:', err);
//     }
//   };

//   useEffect(() => {
//     if (jobId) fetchApplications();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [jobId]);

//   const analyzeAllApplications = async () => {
//     setIsAnalyzingAll(true);
//     try {
//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/application/analyze/job/${jobId}`,
//         {},
//         { withCredentials: true }
//       );
//       setAnalysisResults(res.data.results || {});
//     } catch (err) {
//       console.error('[AI Bulk Analysis] Failed:', err);
//     } finally {
//       setIsAnalyzingAll(false);
//     }
//   };

//   const analyzeSingleApplication = async (applicationId) => {
//     setRowLoading(prev => ({ ...prev, [applicationId]: true }));
//     try {
//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/application/analyze/${applicationId}`,
//         {},
//         { withCredentials: true }
//       );

//       setAnalysisResults(prev => ({
//         ...prev,
//         [applicationId]: res.data.message || '✅ Analysis completed.',
//       }));
//     } catch (err) {
//       console.error(`[AI Single Analysis] Failed for ${applicationId}:`, err);
//       setAnalysisResults(prev => ({
//         ...prev,
//         [applicationId]: '❌ Error analyzing application.',
//       }));
//     } finally {
//       setRowLoading(prev => ({ ...prev, [applicationId]: false }));
//     }
//   };

//   const submitManualCandidate = async (e) => {
//     e.preventDefault();
//     setManualMsg('');

//     if (!mName || !mEmail || !mPhone || !mResume || !mCover) {
//       setManualMsg('Please complete all fields and attach both files.');
//       return;
//     }

//     const formData = new FormData();
//     formData.append('jobId', jobId);
//     formData.append('name', mName);
//     formData.append('email', mEmail);
//     formData.append('phone', mPhone);
//     formData.append('resume', mResume);
//     formData.append('coverLetter', mCover);

//     try {
//       const res = await axios.post(
//         `${process.env.REACT_APP_API}/admin/job/manual-entry`,
//         formData,
//         {
//           headers: { 'Content-Type': 'multipart/form-data' },
//           withCredentials: true
//         }
//       );

//       setManualMsg('✅ Candidate added successfully.');
//       // Clear form
//       setMName(''); setMEmail(''); setMPhone('');
//       setMResume(null); setMCover(null);
//       // Refresh list
//       fetchApplications();
//     } catch (err) {
//       console.error('[Manual Add] Failed:', err);
//       setManualMsg('❌ Failed to add candidate.');
//     }
//   };

//   return (
//     <div style={{ marginTop: '1.5rem', border: '1px solid #ccc', padding: '1rem', borderRadius: '8px' }}>
//       <h3>Applications for Job ID: {jobId}</h3>
//       <button onClick={onClose} style={{ float: 'right' }}>Close</button>
//       <br /><br />

//       {/* Manual Add Panel */}
//       <div style={{ background: '#f9fbff', border: '1px solid #dbe7ff', borderRadius: 8, padding: '1rem', marginBottom: '1rem' }}>
//         <button
//           onClick={() => setShowManualForm(s => !s)}
//           style={{ marginBottom: '0.75rem' }}
//         >
//           {showManualForm ? 'Hide Manual Candidate Form' : '➕ Manually Add Candidate'}
//         </button>

//         {showManualForm && (
//           <form onSubmit={submitManualCandidate}>
//             <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
//               <input type="text" placeholder="Full Name" value={mName} onChange={e => setMName(e.target.value)} required />
//               <input type="email" placeholder="Email" value={mEmail} onChange={e => setMEmail(e.target.value)} required />
//               <input type="tel" placeholder="Phone" value={mPhone} onChange={e => setMPhone(e.target.value)} required />
//               <div />
//               <div>
//                 <label>Resume (PDF/DOC/DOCX)</label>
//                 <input type="file" accept=".pdf,.doc,.docx" onChange={e => setMResume(e.target.files[0])} required />
//               </div>
//               <div>
//                 <label>Cover Letter (PDF/DOC/DOCX)</label>
//                 <input type="file" accept=".pdf,.doc,.docx" onChange={e => setMCover(e.target.files[0])} required />
//               </div>
//             </div>

//             <button type="submit" style={{ marginTop: '0.75rem' }}>Save Candidate</button>
//             {manualMsg && <p style={{ marginTop: '0.5rem' }}>{manualMsg}</p>}
//           </form>
//         )}
//       </div>

//       {applications.length === 0 ? (
//         <p>No applications submitted yet.</p>
//       ) : (
//         <>
//           <button onClick={analyzeAllApplications} disabled={isAnalyzingAll}>
//             {isAnalyzingAll ? 'Analyzing all…' : 'Analyze All Applications'}
//           </button>

//           <ul style={{ marginTop: '1.5rem' }}>
//             {applications.map(app => (
//               <li key={app._id} style={{ marginBottom: '1.5rem' }}>
//                 <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
//                   <div>
//                     <p><strong>Name:</strong> {app.name}</p>
//                     <p><strong>Email:</strong> {app.email}</p>
//                     <p><strong>Phone:</strong> {app.phone}</p>
//                     {app.isManualEntry ? (
//                       <span style={{ padding: '2px 8px', borderRadius: 6, background: '#fff3cd', color: '#856404' }}>
//                         Manually Added
//                       </span>
//                     ) : (
//                       <span style={{ padding: '2px 8px', borderRadius: 6, background: '#e8f5e9', color: '#2e7d32' }}>
//                         Applicant
//                       </span>
//                     )}
//                   </div>

//                   <div>
//                     <a
//                       href={`${process.env.REACT_APP_API}/uploads/jobApplications/${app.resumeFile}`}
//                       target="_blank" rel="noreferrer"
//                     >
//                       📄 Resume
//                     </a>{' '}&nbsp;|&nbsp;{' '}
//                     <a
//                       href={`${process.env.REACT_APP_API}/uploads/jobApplications/${app.coverLetterFile}`}
//                       target="_blank" rel="noreferrer"
//                     >
//                       📝 Cover Letter
//                     </a>
//                   </div>

//                   <div>
//                     <button
//                       onClick={() => analyzeSingleApplication(app._id)}
//                       disabled={!!rowLoading[app._id]}
//                     >
//                       {rowLoading[app._id] ? 'Analyzing…' : 'Analyze This Application'}
//                     </button>
//                   </div>
//                 </div>

//                 {analysisResults[app._id] && (
//                   <div style={{ marginTop: '0.5rem', backgroundColor: '#f8f8f8', padding: '1rem' }}>
//                     <strong>AI Analysis:</strong>
//                     <pre style={{ whiteSpace: 'pre-wrap' }}>{analysisResults[app._id]}</pre>
//                   </div>
//                 )}
//                 <hr />
//               </li>
//             ))}
//           </ul>
//         </>
//       )}
//     </div>
//   );
// };

// export default AdminJobApplicationsViewer;
